import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IMappingAge from '../../interfaces/IMappingAge'
import { SearchRequest, MappingAgeSaveRequest } from '../../services/api/interfaces/ApiRequest'
import { mappingAgeList, mappingAgeSave } from '../../services/api'

interface MappingAgeContextState extends BaseContextState {
  mappingAges: Array<IMappingAge> | null,
  mappingAge: IMappingAge | null
}

interface MappingAgeContextData extends MappingAgeContextState {
  resetErrors: Function,
  requestMappingAgeList: (args: SearchRequest) => Promise<any>,
  requestMappingAgeFind: (args: SearchRequest) => Promise<any>,
  requestMappingAgeSave: (args: MappingAgeSaveRequest) => void,
}

// Create context
const { Provider, Consumer } = createContext({} as MappingAgeContextData)

// Export consumer
export const MappingAgeConsumer = Consumer

export class MappingAgeProvider extends BaseContextProvider {
  state: MappingAgeContextState = {
    mappingAges: null,
    mappingAge: null
  }

  /**
   * Make api request for mapping age list
   * 
   * @param {SearchRequest} payload 
   */
  requestMappingAgeList = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await mappingAgeList(payload)
  
        // Process our response
        this.processResponse(response, { 
          paginate: {
            destination: 'mappingAges'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for mapping age finder
   * 
   * @param {SearchRequest} payload 
   */
  requestMappingAgeFind = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        mappingAge: null
      }, async () => {
        payload.options = {
          ...payload.options,
          _single: true
        }
  
        const response = await mappingAgeList(payload)
  
        // Process our response
        this.processResponse(response, { 
          mappings: {
            destination: 'mappingAge'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for mapping age save
   * 
   * @param {MappingAgeSaveRequest} payload 
   */
  requestMappingAgeSave = (payload: MappingAgeSaveRequest) => {
    this.setState({
      fetching: true,
      error: null,
      mappingAge: null
    }, async () => {
      const response = await mappingAgeSave(payload)

      // Process our response
      this.processResponse(response, {
        mappings: [
          { origin: 'mapping_age', destination: 'mappingAge' }
        ]
      })
    })
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestMappingAgeList: this.requestMappingAgeList,
          requestMappingAgeFind: this.requestMappingAgeFind,
          requestMappingAgeSave: this.requestMappingAgeSave
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
